// import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper, throwFetchError } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { SchemaPresetListResponse } from 'src/types/TSchemes';
import { getSchema, getSchemas, getSchemaPresets, SchemaStatusFilter } from '../functions/schemaApi';

export function useAssetSchemaQuery(
	schemaId: number,
	options?: CustomQueryOptions,
	queryOptions?: UseQueryOptions<any[], FetchError>,
) {
	const actualQueryOptions = mergeQueryOptions(options, queryOptions);
	const accessToken = useAccessToken();

	const queryFn = useMemo(
		() => queryFnFetchErrorWrapper(() => getSchema(accessToken, schemaId)),
		[schemaId, accessToken],
	);

	const query = useQuery<any, FetchError>([queryNames.schema, schemaId], queryFn, actualQueryOptions);

	if (options?.handleErrors && query.status === 'error') {
		if (query.isRefetchError) {
			// Sentry.captureMessage('Failed refetching schema: ' + schemaId, Sentry.Severity.Error);
		} else {
			throwFetchError(query.error);
		}
	}

	return query;
}

/**
 * Hook to fetch the list of asset schemas filtered by status
 */
export function useAssetSchemasListQuery(
	status: SchemaStatusFilter = 'ACTIVE',
	options?: CustomQueryOptions,
	queryOptions?: UseQueryOptions<any, FetchError>,
) {
	const actualQueryOptions = mergeQueryOptions(options, queryOptions);
	const accessToken = useAccessToken();

	const queryFn = useMemo(
		() => queryFnFetchErrorWrapper(() => getSchemas(accessToken, status)),
		[accessToken, status],
	);

	const query = useQuery<any, FetchError>(
		[queryNames.schemas, 'list', status],
		queryFn,
		{
			...actualQueryOptions,
			staleTime: 60000, // 1 minute
		},
	);

	if (options?.handleErrors && query.status === 'error') {
		if (!query.isRefetchError) {
			throwFetchError(query.error);
		}
	}

	return query;
}

/**
 * Hook to fetch available schema presets catalog
 */
export function useSchemaPresetsQuery(
	options?: CustomQueryOptions,
	queryOptions?: UseQueryOptions<SchemaPresetListResponse, FetchError>,
) {
	const actualQueryOptions = mergeQueryOptions(options, queryOptions);
	const accessToken = useAccessToken();

	const queryFn = useMemo(
		() => queryFnFetchErrorWrapper(() => getSchemaPresets(accessToken)),
		[accessToken],
	);

	const query = useQuery<SchemaPresetListResponse, FetchError>(
		[queryNames.schemaPresets, 'list'],
		queryFn,
		{
			...actualQueryOptions,
			staleTime: 300000, // 5 minutes - presets don't change often
		},
	);

	if (options?.handleErrors && query.status === 'error') {
		if (!query.isRefetchError) {
			throwFetchError(query.error);
		}
	}

	return query;
}
